import * as Sentry from '@sentry/react'
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { usePermissions } from '@/hooks/usePermissions'
import { Layout } from '@/components/layout/Layout'
import { Login } from '@/pages/Login'
import { Dashboard } from '@/pages/Dashboard'
import { MemberList } from '@/pages/members/MemberList'
import { MemberForm } from '@/pages/members/MemberForm'
import { MemberDetail } from '@/pages/members/MemberDetail'
import { MembershipList } from '@/pages/memberships/MembershipList'
import { PaymentList } from '@/pages/payments/PaymentList'
import { PaymentForm } from '@/pages/payments/PaymentForm'
import { EventList } from '@/pages/events/EventList'
import { DeviceList } from '@/pages/devices/DeviceList'
import { DeviceDetail } from '@/pages/devices/DeviceDetail'
import { ReportDashboard } from '@/pages/reports/ReportDashboard'
import { Settings } from '@/pages/settings/Settings'
import { SyncPage } from '@/pages/sync/SyncPage'
import { SystemConfig } from '@/pages/admin/SystemConfig'
import { SystemUsers } from '@/pages/admin/users'
import { PlanLimitModal } from '@/components/shared/PlanLimitModal'
import { Unauthorized } from '@/pages/Unauthorized'
import { IS_ELECTRON } from '@/lib/env'

const Router = IS_ELECTRON ? HashRouter : BrowserRouter

function ProtectedRoute({
  element,
  permission,
  can,
}: {
  element: React.ReactElement
  permission?: string
  can: (p: string) => boolean
}) {
  if (permission && !can(permission)) {
    return <Unauthorized />
  }
  return element
}

export default function App() {
  const auth = useAuth()
  const { can } = usePermissions(auth.user)

  if (!auth.isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<Login onLogin={auth.login} />} />
        </Routes>
      </Router>
    )
  }

  return (
    <Sentry.ErrorBoundary fallback={<div className="flex items-center justify-center h-screen text-text-muted text-sm">Algo salió mal. Recarga la página.</div>}>
      <Router>
        <Routes>
          <Route element={<Layout auth={auth} can={can} />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route
              path="/members"
              element={<ProtectedRoute element={<MemberList />} permission="users:read" can={can} />}
            />
            <Route
              path="/members/new"
              element={<ProtectedRoute element={<MemberForm />} permission="users:write" can={can} />}
            />
            <Route
              path="/members/:id"
              element={<ProtectedRoute element={<MemberDetail />} permission="users:read" can={can} />}
            />
            <Route
              path="/members/:id/edit"
              element={<ProtectedRoute element={<MemberForm />} permission="users:write" can={can} />}
            />
            <Route
              path="/memberships"
              element={<ProtectedRoute element={<MembershipList />} permission="memberships:read" can={can} />}
            />
            <Route
              path="/payments"
              element={<ProtectedRoute element={<PaymentList />} permission="payments:read" can={can} />}
            />
            <Route
              path="/payments/new"
              element={<ProtectedRoute element={<PaymentForm />} permission="payments:write" can={can} />}
            />
            <Route
              path="/payments/cash-closings"
              element={<Navigate to="/payments" replace />}
            />
            <Route
              path="/events"
              element={<ProtectedRoute element={<EventList />} permission="events:read" can={can} />}
            />
            <Route
              path="/devices"
              element={<ProtectedRoute element={<DeviceList />} permission="devices:read" can={can} />}
            />
            <Route
              path="/devices/:id"
              element={<ProtectedRoute element={<DeviceDetail />} permission="devices:read" can={can} />}
            />
            <Route
              path="/reports"
              element={<ProtectedRoute element={<ReportDashboard />} permission="users:read" can={can} />}
            />
            <Route
              path="/sync"
              element={<ProtectedRoute element={<SyncPage />} permission="devices:read" can={can} />}
            />
            <Route
              path="/settings"
              element={<ProtectedRoute element={<Settings />} permission="users:*" can={can} />}
            />
            <Route
              path="/admin/config"
              element={<ProtectedRoute element={<SystemConfig />} permission="*" can={can} />}
            />
            <Route
              path="/admin/users"
              element={<ProtectedRoute element={<SystemUsers />} permission="users:write" can={can} />}
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </Router>
      <PlanLimitModal />
    </Sentry.ErrorBoundary>
  )
}
